import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import MenuIcon from "@mui/icons-material/Menu";
import {
  alpha,
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Switch,
  Toolbar,
  Tooltip,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { logout } from "../../api/auth";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { usePermissions } from "../../hooks/Permissions/usePermissions";
import { useUserPreferences } from "../../hooks/Preferences/useUserPreferences";
import { useReferenceSchemas } from "../../hooks/Reference/useReferenceSchemas";
import { resetSessionAuth } from "../../store/Session";
import { StoreInterfaces } from "../../types/store";
import { formatTableName } from "../../utils";
import Converter from "../Converter/Converter";
import LanguageSelector from "../LanguageSelector/LanguageSelector";

interface MenuOptions {
  label: string;
  navigation: string | (() => void);
  subOptions?: MenuOptions[];
  icon?: React.ReactNode;
}

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: "blur(24px)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: "8px 12px",
}));

export default function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const { hasPermission } = usePermissions();
  const session = useSelector((state: StoreInterfaces) => state.session);
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentSubOptions, setCurrentSubOptions] = React.useState<
    MenuOptions[]
  >([]);
  const isAdmin = !!session?.accessToken && session?.isAdministrator;
  const { isEntityAccount } = useAccountPermissions();
  const { schemas, loading } = useReferenceSchemas(isAdmin);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const userName = session?.name ?? session?.email;
  const { preferences, updatePreferences } = useUserPreferences();

  const handleThemeToggle = () => {
    updatePreferences({ ...preferences, theme: preferences?.theme === "dark" ? "light" : "dark" });
  };

  const openUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const closeUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    subOptions?: MenuOptions[],
  ) => {
    if (subOptions) {
      setCurrentSubOptions(subOptions);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentSubOptions([]);
  };

  const handleLogoutClick = () => {
    logout()
      .then(() => {
        dispatch(resetSessionAuth());
        navigate("/");
      })
      .catch((error) => {
        handleError(error);
        dispatch(resetSessionAuth());
        navigate("/");
      });
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const menuOptions = React.useMemo(() => {
    const GUEST_MENU: MenuOptions[] = [
      { label: t("generic.home"), navigation: "/#top" },
    ];

    const MANAGER_MENU: MenuOptions[] = [
      { label: t("generic.home"), navigation: "/home" },
      { 
        label: t("entities.entity-tab"), 
        navigation: "/entities/entity-list"
      },
      {
        label: "BDSUB",
        navigation: () => {},
        subOptions: [
          { label: t("generic.captures"), navigation: "/captures/capture-list" },
          { label: t("exports.label"), navigation: "/exports" },
        ],
      },
      ...(hasPermission("MODULE_WAREHOUSE") 
        ? [{ label: t("warehouse.equipment.title"), navigation: "/warehouse" }] 
        : []),
    ];

    const ENTITY_USER_MENU: MenuOptions[] = [
      {
        label: t("entities.my-entity"),
        navigation: "/entities/entity-list",
      },
      {
        label: "BDSUB",
        navigation: () => {},
        subOptions: [
          { label: t("generic.captures"), navigation: "/captures/capture-list" },
          { label: t("exports.label"), navigation: "/exports" },
        ],
      },
    ];

    if (!session?.accessToken) {
      return GUEST_MENU;
    }

    if (isEntityAccount) {
      return ENTITY_USER_MENU;
    }

    if (!session.isAdministrator) {
      return MANAGER_MENU;
    }

    if (!loading) {
      const refSubOptions: MenuOptions[] = Object.entries(schemas).map(
        ([schemaName, tables]) => ({
          label: schemaName,
          navigation: `/admin/reference/${schemaName}`,
          subOptions: tables.map((table) => ({
            label: formatTableName(table.tableName),
            navigation: `/admin/reference/${table.route}`,
          })),
        }),
      );

      const ADMIN_MENU: MenuOptions[] = [
        { label: t("generic.home"), navigation: "/home" },
        { label: t("admin.accounts.label"), navigation: "admin/accounts" },
        { label: t("admin.users.label"), navigation: "admin/users" },
        {
          label: t("admin.reference.title"),
          navigation: () => {},
          subOptions: refSubOptions,
        },
        {
          label: t("generic.more"),
          navigation: () => {},
          icon: <MoreHorizRoundedIcon fontSize="small" />,
          subOptions: [
            {
              label: t("admin.entity-permissions.label"),
              navigation: "/admin/entity-permissions",
            },
            {
              label: t("admin.accounts.account-tab-audit"),
              navigation: "/admin/audit",
            },
            {
              label: t("admin.variables.title"),
              navigation: "admin/reference/variables",
            },
            {
              label: t("exports.label"),
              navigation: "admin/exports",
            },
          ],
        },
      ];

      return ADMIN_MENU;
    }
    return [];
  }, [session, t, schemas, loading, hasPermission, isEntityAccount]);

  const handleNavigation = (navigation: string | (() => void)) => {
    if (typeof navigation === "function") {
      navigation();
    } else {
      navigate(navigation);
    }
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: "transparent",
        backgroundImage: "none",
        mt: "calc(var(--template-frame-height, 0px) + 28px)",
      }}
    >
      <Container>
        <StyledToolbar variant="dense" disableGutters>
          <Box
            sx={{ flexGrow: 1, display: { xs: "none", md: "flex" }, gap: 1 }}
          >
            {menuOptions.map((option, i) => (
              <Box key={i}>
                <Button
                  color="primary"
                  size="small"
                  variant="text"
                  startIcon={option.icon}
                  onClick={(e) =>
                    option.subOptions
                      ? handleMenuOpen(e, option.subOptions)
                      : handleNavigation(option.navigation)
                  }
                >
                  {option.label}
                </Button>
              </Box>
            ))}

            {/* Dropdown */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {currentSubOptions.map((sub, idx) => (
                <MenuItem
                  key={idx}
                  onClick={() => {
                    handleMenuClose();
                    handleNavigation(sub.navigation);
                  }}
                >
                  {sub.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Covertor */}
          {session?.accessToken && <Converter />}

          {/* Seletor de Idioma */}
          <LanguageSelector />

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
            {session?.accessToken ? (
              <> 
                <Tooltip title={userName}>
                  <Button
                    color="primary"
                    size="small"
                    onClick={openUserMenu}
                    endIcon={<AccountCircleIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    <Box
                      component="span"
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {userName}
                    </Box>
                  </Button>
                </Tooltip>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={closeUserMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => {
                      closeUserMenu();
                      navigate("/profile");
                    }}
                  >
                    {t("generic.profile")}
                  </MenuItem>

                  <Divider />

                  <MenuItem 
                    onClick={handleThemeToggle} 
                    sx={{
                      gap: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      mx: 1,
                      my: 0.5,
                    }}>
                    <LightModeIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Switch
                      checked={preferences?.theme === "dark"}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      onChange={handleThemeToggle}
                    />
                    <DarkModeIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MenuItem>

                  <Divider />

                  <MenuItem
                    onClick={() => {
                      closeUserMenu();
                      handleLogoutClick();
                    }}
                  >
                    {t("generic.logout")}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="primary"
                size="small"
                onClick={() => navigate("/login")}
              >
                {t("generic.login.title")}
              </Button>
            )}
          </Box>

          {/* Menu Hamburguer (Mobile) */}
          <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1 }}>
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{ sx: { top: "var(--template-frame-height, 0px)" } }}
            >
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "background.default",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                {session?.accessToken && (
                  <>
                    <MenuItem disabled>
                      {userName}
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        navigate("/profile");
                        toggleDrawer(false)();
                      }}
                    >
                      {t("generic.profile")}
                    </MenuItem>

                    <Divider />
                  </>
                )}
                {menuOptions.map((option, index) =>
                  option.subOptions ? (
                    <React.Fragment key={"MenuGroup" + index}>
                      <MenuItem disabled>{option.label}</MenuItem>
                      {option.subOptions.map((sub, subIndex) => (
                        <MenuItem
                          key={`MenuSub${index}-${subIndex}`}
                          sx={{ pl: 4 }}
                          onClick={() => {
                            handleNavigation(sub.navigation);
                            toggleDrawer(false)();
                          }}
                        >
                          {sub.label}
                        </MenuItem>
                      ))}
                    </React.Fragment>
                  ) : (
                    <MenuItem
                      key={"MenuItem" + index}
                      onClick={() => {
                        handleNavigation(option.navigation);
                        toggleDrawer(false)();
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ),
                )}

                <Divider sx={{ my: 3 }} />

                <MenuItem>
                  <Button
                    color="primary"
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      if (session?.accessToken) {
                        handleLogoutClick();
                      } else {
                        navigate("/login");
                      }
                      toggleDrawer(false)();
                    }}
                  >
                    {session?.accessToken
                      ? t("generic.logout")
                      : t("generic.login.title")}
                  </Button>
                </MenuItem>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
